import { EyeOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { useBulkContentTakeDownOrDelete } from '@/features/content/api';

interface BulkUserContentActionsProps {
  contentIds: number[];
  onClearSelection: () => void;
}

/**
 * Toolbar for multi-select Take Down / Delete on User Detail → All Content.
 * Uses the same override + TER path as content detail moderation actions.
 */
export function BulkUserContentActions({
  contentIds,
  onClearSelection,
}: BulkUserContentActionsProps) {
  const confirm = useConfirm();
  const bulkMut = useBulkContentTakeDownOrDelete();

  if (contentIds.length === 0) return null;

  const count = contentIds.length;
  const pending = bulkMut.isPending;

  async function onTakeDown() {
    const { confirmed, reason } = await confirm({
      title: `Take Down ${count} post${count === 1 ? '' : 's'}`,
      description:
        'This will hide the selected content from all users and close any open Coop Review Console jobs.',
      confirmText: 'Take Down',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    try {
      const result = await bulkMut.mutateAsync({
        contentIds,
        mode: 'takeDown',
        reason: reason?.trim() || 'Admin take down',
      });
      if (result.failed === 0) {
        toast.success(
          result.total === 1
            ? 'Content taken down.'
            : `Took down ${result.succeeded} posts.`,
        );
      } else {
        toast.warning(
          `Took down ${result.succeeded} of ${result.total}; ${result.failed} failed.`,
        );
      }
      onClearSelection();
    } catch {
      toast.error('Failed to take down selected content.');
    }
  }

  async function onDelete() {
    const { confirmed, reason } = await confirm({
      title: `Permanently Delete ${count} post${count === 1 ? '' : 's'}`,
      description:
        'This permanently terminates the selected content and closes any open Coop Review Console jobs. This action cannot be undone.',
      confirmText: 'Delete Permanently',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    try {
      const result = await bulkMut.mutateAsync({
        contentIds,
        mode: 'delete',
        reason: reason?.trim() || 'Admin terminate',
      });
      if (result.failed === 0) {
        toast.success(
          result.total === 1
            ? 'Content permanently deleted.'
            : `Deleted ${result.succeeded} posts.`,
        );
      } else {
        toast.warning(
          `Deleted ${result.succeeded} of ${result.total}; ${result.failed} failed.`,
        );
      }
      onClearSelection();
    } catch {
      toast.error('Failed to delete selected content.');
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">{count} selected</span>
      <Button size="sm" variant="destructive" disabled={pending} onClick={() => void onTakeDown()}>
        <EyeOff className="size-4" />
        Take Down
      </Button>
      <Button size="sm" variant="destructive" disabled={pending} onClick={() => void onDelete()}>
        <Trash2 className="size-4" />
        Delete
      </Button>
      <Button size="sm" variant="ghost" disabled={pending} onClick={onClearSelection}>
        Clear
      </Button>
    </div>
  );
}
